import { useEffect } from 'react';
import { CONSENT_EVENT, getConsent, type ConsentValue } from '~/lib/consent';
import { initFirebaseAnalytics } from '~/lib/firebase';

// Third-party analytics and marketing scripts ported from the old Webflow site.
// Cookieless tools load immediately on live hosts; cookie-setting / PII trackers
// load only after consent. Client-side injection avoids SSR hydration mismatches.

const PROD_HOSTS = ['website.dev.nsave.com', 'nsave.com', 'www.nsave.com'];

const GTM_CONTAINER_ID = 'GTM-P8RP7K4V';
const GA_MEASUREMENT_ID = 'G-KE17NLRZ32';
const PLAUSIBLE_DOMAIN = 'nsave.com';
const AHREFS_KEY = 'aXmFQAj8nar72Ub99bXyMA';
const FB_APP_ID = '964897222376610';
const META_PIXEL_ID = '1326939099590151';
const CLARITY_SRC =
  'https://cdn.prod.website-files.com/66de9fe4a74bb719d845a843%2F652d31f3dc22d7b4ee708e44%2F6a2340e41c9787a31dcc3f10%2Fclarity_script-2.9.8.js';

declare global {
  interface Window {
    __nsaveCookielessLoaded?: boolean;
    __nsaveGatedLoaded?: boolean;
  }
}

function addScript(options: {
  src?: string;
  inline?: string;
  async?: boolean;
  defer?: boolean;
  attrs?: Record<string, string>;
}) {
  const element = document.createElement('script');
  if (options.src) element.src = options.src;
  if (options.inline) element.text = options.inline;
  if (options.async) element.async = true;
  if (options.defer) element.defer = true;
  if (options.attrs) {
    for (const [key, value] of Object.entries(options.attrs)) {
      element.setAttribute(key, value);
    }
  }
  document.head.appendChild(element);
}

function loadCookieless() {
  if (window.__nsaveCookielessLoaded) return;
  window.__nsaveCookielessLoaded = true;

  addScript({
    src: 'https://plausible.io/js/script.js',
    defer: true,
    attrs: { 'data-domain': PLAUSIBLE_DOMAIN },
  });
  addScript({
    src: 'https://analytics.ahrefs.com/analytics.js',
    async: true,
    attrs: { 'data-key': AHREFS_KEY },
  });
}

function loadConsented() {
  if (window.__nsaveGatedLoaded) return;
  window.__nsaveGatedLoaded = true;

  addScript({
    inline: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_CONTAINER_ID}');`,
  });
  addScript({
    src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
    async: true,
  });
  addScript({
    inline: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA_MEASUREMENT_ID}');`,
  });
  addScript({ src: CLARITY_SRC });

  if (!document.getElementById('fb-root')) {
    const facebookRoot = document.createElement('div');
    facebookRoot.id = 'fb-root';
    document.body.insertBefore(facebookRoot, document.body.firstChild);
  }
  addScript({
    inline: `window.fbAsyncInit=function(){FB.init({appId:'${FB_APP_ID}',xfbml:true,version:'v22.0'});FB.AppEvents.logPageView();};(function(d,s,id){var js,fjs=d.getElementsByTagName(s)[0];if(d.getElementById(id)){return;}js=d.createElement(s);js.id=id;js.src="https://connect.facebook.net/en_US/sdk.js";fjs.parentNode.insertBefore(js,fjs);}(document,'script','facebook-jssdk'));`,
  });
  addScript({
    inline: `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${META_PIXEL_ID}');fbq('track','PageView');`,
  });

  // Firebase Analytics (GA4 — Firebase project `nsave1`). Team can use other
  // Firebase products anytime via getFirebaseApp() in ~/lib/firebase.
  void initFirebaseAnalytics();

  // Preserve the website's existing Leadsy integration, but gate it with the
  // other visitor-identification trackers instead of loading it before consent.
  if (window.location.hostname === 'www.nsave.com') {
    const leadsy = document.createElement('script');
    leadsy.id = 'vtag-ai-js';
    leadsy.async = true;
    leadsy.src = 'https://r2.leadsy.ai/tag.js';
    leadsy.dataset.pid = '1rfsxjcMjuHtXnZE4';
    leadsy.dataset.version = '062024';
    document.body.append(leadsy);
  }
}

export function Analytics() {
  useEffect(() => {
    if (!PROD_HOSTS.includes(window.location.hostname)) return;

    loadCookieless();
    if (getConsent() === 'accepted') loadConsented();

    const onConsent = (event: Event) => {
      if ((event as CustomEvent<ConsentValue>).detail === 'accepted') loadConsented();
    };
    window.addEventListener(CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(CONSENT_EVENT, onConsent);
  }, []);

  return null;
}
